import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { PageHeader, Loading, Empty } from "../components/Layout";
import { BandBadge, StatusChip } from "../components/Badges";
import { NewLeadModal } from "../components/NewLeadModal";
import { Plus } from "lucide-react";

function LeadTable({ leads, nav, testid }) {
  if (!leads) return <Loading />;
  if (leads.length === 0) return (
    <div className="flex flex-col items-center justify-center text-center py-20" data-testid="queue-empty">
      <h2 className="text-2xl font-light text-[#1C2530] mb-2">Nothing in your queue yet</h2>
      <p className="text-base text-[#1C2530]/55 max-w-md mb-6">Leads assigned to you show up here, sorted by priority. Add your first lead or import a list to get started.</p>
      <button onClick={() => nav("/import")} data-testid="queue-empty-import" className="bg-[#1C2530] text-[#F5F3EE] px-5 py-2.5 text-sm rounded-full hover:bg-[#2a3644] transition-colors">Import leads</button>
    </div>
  );
  return (
    <div className="border border-[#DFDAD0] bg-white overflow-hidden" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#EFECE5] font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">
            <th className="text-left px-4 py-2.5">Account</th>
            <th className="text-left px-4 py-2.5">Contact</th>
            <th className="text-left px-4 py-2.5">Stage</th>
            <th className="text-left px-4 py-2.5">Band</th>
            <th className="text-right px-4 py-2.5">Score</th>
            <th className="text-left px-4 py-2.5">Status</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l, i) => (
            <tr key={l.id} onClick={() => nav(`/leads/${l.id}`)} data-testid={`queue-row-${l.id}`}
              className={`cursor-pointer hover:bg-[#EFECE5] border-t border-[#DFDAD0] ${i % 2 ? "bg-[#F5F3EE]/40" : "bg-white"}`}>
              <td className="px-4 py-2.5 font-medium text-[#1C2530]">{l.account?.name}</td>
              <td className="px-4 py-2.5 text-[#1C2530]/70">{l.contact?.name}<span className="text-[#1C2530]/40"> · {l.contact?.title}</span></td>
              <td className="px-4 py-2.5"><span className="font-mono text-xs">{l.stage}</span></td>
              <td className="px-4 py-2.5"><BandBadge band={l.band} /></td>
              <td className="px-4 py-2.5 text-right font-mono tabular-nums">{l.total_score}</td>
              <td className="px-4 py-2.5"><StatusChip status={l.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MyQueue() {
  const [leads, setLeads] = useState(null);
  const [regions, setRegions] = useState([]);
  const [modal, setModal] = useState(false);
  const nav = useNavigate();
  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); api.get("/regions").then((r) => setRegions(r.data)).catch(() => {}); }, []);
  const sorted = leads ? [...leads].sort((a, b) => b.total_score - a.total_score) : null;
  return (
    <>
      <PageHeader eyebrow="ASSIGNED TO YOU · BY PRIORITY" title="My Queue"
        right={<button onClick={() => setModal(true)} data-testid="new-lead-btn" className="flex items-center gap-1.5 bg-[#E50914] text-white px-4 py-2 text-sm font-medium rounded-sm hover:bg-[#c40810]"><Plus className="w-4 h-4" /> New Lead</button>} />
      <div className="p-8"><LeadTable leads={sorted} nav={nav} testid="rep-queue-table" /></div>
      {modal && <NewLeadModal regions={regions} onClose={() => setModal(false)} onCreated={load} />}
    </>
  );
}

export { LeadTable };
